import axios, { type AxiosPromise } from "axios";
import debounce from "lodash/debounce";
import React from "react";

import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { Message } from "semantic-ui-react";
import { type UploadFilesConfig } from "utilities/fileUploadUtils";
import { DeleteDialog } from "../../components";
import { NoDataAvailableDiv } from "../../components/NoDataAvailableDiv";
import Panel from "../../components/panels/panel";
import { usePagination } from "../../utilities/hookutils";
import FileEdit from "./fileedit";
import FileRow from "./filerow";
import UploadingRow from "./uploadingrow";
import * as Sentry from "@sentry/browser";


interface Props {
  config: server.dto.LinkedFilePanelConfig
  standalone?: boolean
  headerSize?: "h1" | "h2"
  fetchFilesApi: (page: number, per_page: number) => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.File[]>>
  uploadFiles: (file: File[], uploadConfig: UploadFilesConfig) => AxiosPromise<server.dto.ApiResponse>
}

const FilesPanel = (props: Props) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [startFileUpload, setStartFileUpload] = React.useState(false);
  const [files, setFiles] = React.useState<server.dto.File[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isDraggingFileOver, setIsDraggingFileOver] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string>(undefined);
  const [uploadFileNames, setUploadFileNames] = React.useState<string[]>([]);
  const [isFinishedUploading, setIsFinishedUploading] = React.useState(false);
  const [deletingFileId, setDeletingFileId] = React.useState<number>(undefined);
  const [editedFileId, setEditedFileId] = React.useState<number>(undefined);
  const [hasScrolled, setHasScrolled] = React.useState(false);

  const fileUploadRef = React.useRef<HTMLInputElement>();

  const { refToScrollTo } = useShortcutPanel({
    type: "file",
    label: props.config.headerLabel,
    totalItems: files.length,
    onAddClicked: props.config.canUpload ? () => { fileUploadRef.current?.click(); } : undefined
  });

  React.useEffect(() => {
    document.body.addEventListener("dragenter", dragEnter);
    document.body.addEventListener("dragover", dragOver.current);
    return () => {
      document.body.removeEventListener("dragenter", dragEnter);
      document.body.removeEventListener("dragover", dragOver.current);
    };
  }, [hasScrolled, refToScrollTo]);

  React.useEffect(() => {
    window.onSavePdfToOnlineStorage = onSavePdfToOnlineStorage;
    window.startFileUpload = () => {
      setStartFileUpload(true);
    };
  }, []);

  React.useEffect(() => {
    if (startFileUpload) {
      setStartFileUpload(false);
      fileUploadRef.current?.click();
    }
  }, [startFileUpload]);

  const onSavePdfToOnlineStorage = async (url: string, fileName: string) => {
    setIsUploading(true);
    setIsFinishedUploading(false);
    setUploadFileNames([fileName]);

    try {
      await axios.get(url, {
        withCredentials: true
      });
      setIsFinishedUploading(true);
    } catch {
      setUploadError(RESX.GeneralLabels.SomethingUnexpectedHappened);
      setIsUploading(false);
      setUploadFileNames([]);
    }
  };

  const dragEnter = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) {
      return;
    }

    if (!props.config.canUpload) {
      return;
    }

    setIsDraggingFileOver(true);

    if (!hasScrolled) {
      setHasScrolled(true);
      scrollToFilesPanel();
    }
  };

  const dragEnd = () => {
    setIsDraggingFileOver(false);
    setHasScrolled(false);
  };

  const dragOver = React.useRef(debounce(dragEnd, 200));

  const paginate = async (pagination: Partial<server.dto.PaginationData>) => {
    const res = await props.fetchFilesApi(pagination.page, props.config.perPage);
    setIsLoading(false);
    setFiles(res.data.data);

    return res.data.pagination;
  };

  const scrollToFilesPanel = () => {
    if (refToScrollTo.current) {
      window.scrollTo({
        behavior: "smooth",
        top: refToScrollTo.current.offsetTop - 30
      });
    }
  };

  const onFilesUpload = async (fileList: File[]) => {
    if (fileList.length === 0) {
      setIsDraggingFileOver(false);
      return;
    }

    const fileNames = Array.from(fileList).map(file => file.name);

    setIsDraggingFileOver(false);
    setUploadFileNames(fileNames);
    setIsFinishedUploading(false);
    setUploadError(undefined);

    const config: UploadFilesConfig = {
      fileType: "any"
    };

    setIsUploading(true);
    try {
      const uploadFileRes = await props.uploadFiles(fileList, config);
      if (uploadFileRes.status !== 200 || uploadFileRes.data.hasErrors) {
        setUploadError(uploadFileRes.data.errors[0]?.message);
        setIsUploading(false);
        return;
      }
      setIsFinishedUploading(true);
    } catch (e) {
      Sentry.captureException(e);
      setUploadError(RESX.GeneralLabels.SomethingUnexpectedHappened);
      setIsUploading(false);
    }
    setUploadFileNames([]);
  };

  const pagination = usePagination(props.config.perPage, paginate);

  const showFiles = !isUploading && !isLoading && !isDraggingFileOver;

  const onFilesUploadClick = () => {
    if (fileUploadRef.current) {
      onFilesUpload(Array.from(fileUploadRef.current.files));
    }
  };

  const deleteFile = async () => {
    return window.CoreApi.File.DeleteClientFile(deletingFileId)
      .then(() => {
        setDeletingFileId(undefined);
        pagination.onPaginate(pagination.page);
      });
  };

  const onEditSaved = async (clientFileId: number, name: string, visibleToExternal: boolean) => {
    return window.CoreApi.File.SetClientFileProperties({
      clientFileId,
      visibleToExternal,
      name
    }).then(() => {
      pagination.onPaginate(pagination.page);
      setEditedFileId(undefined);
    });
  };

  const onProgressFinished = async () => {
    await pagination.onPaginate(pagination.page);
    setIsUploading(false);
  };

  const onEditClicked = (file: server.dto.File) => { setEditedFileId(file.id); };
  const onDeleteClicked = (file: server.dto.File) => { setDeletingFileId(file.id); };

  return <Panel
    title={props.config.headerLabel}
    headerSize={props.headerSize}
    panelClassName={props.standalone ? "s1_listview-panel" : "s1_related-feature-panel"}
    pagination={pagination.Component}
    isLoading={isLoading}
    panelRef={refToScrollTo}
    button={props.config.canUpload
      ? (
        props.standalone
          ? <ButtonPrimary label={RESX.Buttons.Upload} onClick={() => { fileUploadRef.current?.click(); }} />
          : <ButtonSecondary label={RESX.Buttons.Upload} onClick={() => { fileUploadRef.current?.click(); }} />
      )
      : null}
  >
    {props.config.canUpload && <input
      type="file"
      multiple
      style={{ display: "none" }}
      ref={fileUploadRef}
      onChange={onFilesUploadClick}
    />}
    <div>
      <DeleteDialog
        isOpen={deletingFileId != null}
        onDelete={deleteFile}
        message={RESX.GeneralWarnings.DeleteSure}
        handleClose={() => { setDeletingFileId(undefined); }}
      />

      {showFiles && <>
        {uploadError && <Message color="red" className="margin-bottom">
          {uploadError}
        </Message>}
        <div className="list-with-gaps line-layout">
          <div className="list-with-gaps-body">
            {files.map(file => (
              editedFileId === file.id
                ? <FileEdit key={file.id} config={props.config} initialFile={file} onEdit={onEditSaved} onDelete={onDeleteClicked} />
                : <FileRow key={file.id} config={props.config} file={file} onEdit={onEditClicked} onDelete={onDeleteClicked} />
            ))}
          </div>
        </div>
      </>}

      {(showFiles && files.length === 0) &&
        <NoDataAvailableDiv />}

      {isUploading && <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          <UploadingRow fileNames={uploadFileNames} hasFinishedUploading={isFinishedUploading} onFinish={onProgressFinished} />
        </div>
      </div>}

      {isDraggingFileOver && <FileDropzone
        onDrop={onFilesUpload}
        onDropReject={(fileRejections) => {
          setUploadError(fileRejections[0].errors[0].message);
        }}
        maxSizeBytes={serverReference.constants.file.maximumClientFileContentLength}
        iconType="file"
      />}
    </div>
  </Panel>;
};

export default FilesPanel;